import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Archive } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRangeQuery } from "@/lib/useData";
import { useUrlFilters } from "@/lib/useUrlFilters";
import { FilterBar } from "@/components/shared/FilterBar";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { RowMenu } from "@/components/kit";
import {
  Card, SectionTitle, Takeaway, MetricTip, GroupTooSmall,
  LoadingBlocks, ErrorState, Forbidden, EmptyState,
} from "@/components/shared/primitives";
import { AdoptionRanking } from "@/components/shared/charts";
import { pct, METRIC_DEFS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function CreateTeamSheet({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [filters, setFilters] = useState({ seniority: "all", location: "all", search: "" });
  const [opts, setOpts] = useState({ seniorities: [], locations: [] });
  const [eligible, setEligible] = useState([]);
  const [selected, setSelected] = useState([]);

  const loadEligible = useCallback(() => {
    const p = {};
    if (filters.seniority !== "all") p.seniority = filters.seniority;
    if (filters.location !== "all") p.location = filters.location;
    if (filters.search) p.search = filters.search;
    api.get("/teams/eligible", p).then((r) => setEligible(r.data)).catch(() => setEligible([]));
  }, [filters]);

  useEffect(() => {
    if (open) {
      setForm({ name: "", description: "" }); setSelected([]); setFilters({ seniority: "all", location: "all", search: "" });
      api.get("/teams/filter-options").then((r) => setOpts(r.data)).catch(() => {});
    }
  }, [open]);
  useEffect(() => { if (open) loadEligible(); }, [open, loadEligible]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Give the team a name."); return; }
    try {
      await api.post("/teams", { name: form.name, description: form.description, member_ids: selected });
      toast.success(`Team created${selected.length ? `, ${selected.length} invited` : ""}.`);
      onCreated(); onClose();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-nb-surface w-[520px] sm:max-w-[520px] overflow-y-auto">
        <SheetHeader><SheetTitle className="text-nb-ink font-display">Create team</SheetTitle></SheetHeader>
        <div className="space-y-5 mt-6">
          <div><Label className="text-[13px]">Team name</Label><Input data-testid="team-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="e.g. AI Champions" /></div>
          <div><Label className="text-[13px]">Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="mt-1" /></div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[13px]">Invite members</Label>
              <span className="text-[12px] text-nb-ink2 tnum">{selected.length} selected</span>
            </div>
            <div className="flex gap-2 mb-2">
              <Select value={filters.seniority} onValueChange={(v) => setFilters((f) => ({ ...f, seniority: v }))}>
                <SelectTrigger className="h-9 flex-1" data-testid="eligible-seniority"><SelectValue placeholder="All seniority" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All seniority</SelectItem>{opts.seniorities.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filters.location} onValueChange={(v) => setFilters((f) => ({ ...f, location: v }))}>
                <SelectTrigger className="h-9 flex-1" data-testid="eligible-location"><SelectValue placeholder="All offices" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All offices</SelectItem>{opts.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input placeholder="Search by name" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="h-9 mb-2" />
            <div className="rounded-xl max-h-[260px] overflow-y-auto" style={{ border: "1px solid var(--border-subtle)" }}>
              {eligible.length === 0 ? <p className="text-[13px] text-nb-ink2 p-3">No eligible members match.</p> :
                eligible.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-0 cursor-pointer hover:bg-[var(--bg-selected)]" style={{ borderColor: "var(--border-subtle)" }} data-testid={`eligible-${m.id}`}>
                    <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} />
                    <span className="flex-1 text-[14px] text-nb-ink">{m.name}</span>
                    <span className="text-[12px] text-nb-ink2">{m.seniority} · {m.location}{m.team ? ` · ${m.team}` : ""}</span>
                  </label>
                ))}
            </div>
          </div>

          <Button onClick={submit} className="w-full rounded-[10px] bg-nb-navy text-nb-cream font-semibold" data-testid="create-team-submit">Create team and invite</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function TeamsPanel() {
  const { role, member } = useAuth();
  const canCreate = role === "admin" || role === "team_lead";
  const [creating, setCreating] = useState(false);
  const [archiving, setArchiving] = useState(null);
  const { values, setFilter, clearAll } = useUrlFilters(["office", "sort"]);
  const { data, isLoading, error, refetch } = useRangeQuery("teams", "/teams");
  const { data: officesData } = useQuery({ queryKey: ["offices"], queryFn: () => api.get("/offices") });
  const officeOptions = (officesData?.data || []).map((o) => ({ value: o, label: o }));

  const rawRows = useMemo(() => data?.data?.rows || [], [data]);
  const rows = useMemo(() => {
    let list = [...rawRows];
    if (values.office) list = list.filter((t) => (t.offices || []).includes(values.office));
    const sort = values.sort || "adoption_high";
    const score = (t) => (t.group_too_small ? -1 : t.adoption ?? 0);
    const cmp = {
      adoption_high: (a, b) => score(b) - score(a),
      adoption_low: (a, b) => (a.group_too_small ? 1 : 0) - (b.group_too_small ? 1 : 0) || score(a) - score(b),
      name: (a, b) => a.team.localeCompare(b.team),
      members: (a, b) => b.members - a.members,
    }[sort] || ((a, b) => score(b) - score(a));
    return list.sort(cmp);
  }, [rawRows, values.office, values.sort]);

  const canManage = (t) => role === "admin" || t.created_by === member?.id;
  const firmPeople = useMemo(() => {
    const big = rawRows.filter((t) => !t.group_too_small);
    const using = big.reduce((s, t) => s + (t.members_using || 0), 0);
    const tot = big.reduce((s, t) => s + (t.members || 0), 0);
    return tot ? using / tot : 0;
  }, [rawRows]);
  const doArchive = async () => {
    try { await api.del(`/teams/${archiving.team_id}`); toast.success(`${archiving.team} archived.`); refetch(); }
    catch (e) { toast.error(e.message); }
    setArchiving(null);
  };

  if (error?.code === "forbidden") return <Forbidden who="Admins, IT Admins and Partners" />;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-2">
        <Takeaway>Which teams have taken AI on board? Adoption is members using any AI tool, divided by team members.</Takeaway>
        {canCreate && (
          <Button onClick={() => setCreating(true)} data-testid="create-team-btn" className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2 shrink-0">
            <Plus size={16} strokeWidth={1.5} /> Create team
          </Button>
        )}
      </div>
      {isLoading && <LoadingBlocks rows={4} />}
      {error && <ErrorState error={error} onRetry={refetch} />}
      {data && rawRows.length === 0 && (
        <EmptyState title="No teams yet." next="Add teams and people in Settings, Teams and people." />
      )}

      {data && rawRows.length > 0 && (
        <div className="space-y-8">
          <FilterBar
            values={values}
            onChange={setFilter}
            onClear={clearAll}
            filters={[
              { key: "office", label: "Office", placeholder: "All offices", options: officeOptions },
            ]}
            sort={{ key: "sort", label: "Sort by", options: [
              { value: "adoption_high", label: "Highest adoption" },
              { value: "adoption_low", label: "Lowest adoption" },
              { value: "name", label: "Alphabetical" },
              { value: "members", label: "Most members" },
            ] }}
          />

          <Card className="p-5" data-testid="teams-table">
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="nb-label text-left" style={{ background: "var(--bg-sunken)" }}>
                    <th className="py-2.5 px-3 first:rounded-l-md">Team</th>
                    <th className="py-2.5 px-3 text-right">Members</th>
                    <th className="py-2.5 px-3 text-right">Using AI</th>
                    <th className="py-2.5 px-3 text-right"><MetricTip label="Adoption" def={METRIC_DEFS.adoption} /></th>
                    <th className="py-2.5 px-3 text-right">Tools used</th>
                    <th className="py-2.5 px-3">Most-used feature</th>
                    <th className="py-2.5 px-3 last:rounded-r-md"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.team_id} className="border-b last:border-0 transition-colors hover:bg-[var(--bg-selected)]" style={{ borderColor: "var(--border-subtle)" }} data-testid={`team-row-${t.team_id}`}>
                      <td className="py-3 px-3"><Link to={`/teams/${t.team_id}`} className="text-nb-ink hover:text-[var(--text-link)] font-medium">{t.team}</Link></td>
                      <td className="py-3 px-3 tnum text-right">{t.members}</td>
                      {t.group_too_small ? (
                        <td colSpan={4} className="py-3 px-3"><GroupTooSmall /></td>
                      ) : (
                        <>
                          <td className="py-3 px-3 tnum text-right">{t.members_using}</td>
                          <td className="py-3 px-3 text-right">
                            <span className="inline-flex items-center gap-2 justify-end">
                              <span className="tnum">{pct(t.adoption)}</span>
                              <span className="relative hidden sm:inline-block h-2 w-20 rounded-full" style={{ background: "var(--bg-sunken)" }}>
                                <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: pct(t.adoption), background: "var(--brand-kawaii)" }} />
                                <span className="absolute inset-y-[-2px] border-l border-dashed" style={{ left: pct(firmPeople), borderColor: "var(--brand-navy)" }} />
                              </span>
                            </span>
                          </td>
                          <td className="py-3 px-3 tnum text-right">{t.tools_used}</td>
                          <td className="py-3 px-3">{t.most_used_feature || "-"}</td>
                        </>
                      )}
                      <td className="py-3 px-3 text-right">
                        {canManage(t) && (
                          <RowMenu testid={`team-menu-${t.team_id}`} items={[
                            { label: "Archive team\u2026", destructive: true, testid: `archive-team-${t.team_id}`,
                              icon: <Archive size={15} className="mr-2" />, onSelect: () => setArchiving(t) },
                          ]} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {rawRows.some((t) => !t.group_too_small) && (
            <Card className="p-5" data-testid="teams-adoption-ranking">
              <SectionTitle className="mb-1">Adoption ranking</SectionTitle>
              <Takeaway>Share of each team using any AI tool, ranked against the firm average. Teams of fewer than five are left out.</Takeaway>
              <AdoptionRanking rows={rawRows} firm={firmPeople} testid="adoption-ranking" />
            </Card>
          )}
        </div>
      )}
      <CreateTeamSheet open={creating} onClose={() => setCreating(false)} onCreated={refetch} />
      <ConfirmDialog
        open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}
        title={`Archive ${archiving?.team}?`}
        description="The team is hidden from lists but its members and their past usage are kept. You can restore it from the team page."
        confirmLabel="Archive team" onConfirm={doArchive}
      />
    </div>
  );
}
